import { useEffect, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";

import { ThemeContext } from "../contexts";

export function ThemeProvider({ children }: IGlobalProvider) {
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const [mode, setMode] = useState(prefersDarkMode ? "dark" : "light");

    const theme = createTheme({
        palette: {
            mode: mode === "dark" ? "dark" : "light",
            text: {
                primary: mode === "dark" ? "#f0f0f0" : "#2d2d2d",
            },
            primary: {
                main: mode === "dark" ? "#ffb347" : "#845ef7",
            },
            secondary: {
                main: "#ffb347",
            },
            background: {
                default: mode === "dark" ? "#1e1e2e" : "#fef9ef",
                paper: mode === "dark" ? "#2a2a3e" : "#ffffff",
            },
        },
        shape: {
            borderRadius: 16,
        },
        typography: {
            fontFamily: ['"Short Stack"', '"Caveat"', "cursive"].join(","),
            fontSize: 14,
            h3: {
                fontFamily: '"Caveat", "Short Stack", cursive',
                fontWeight: 800,
                letterSpacing: "-0.02em",
            },
            h6: {
                fontFamily: '"Caveat", "Short Stack", cursive',
                fontWeight: 700,
            },
            body1: {
                fontFamily: '"Short Stack", "Caveat", cursive',
                fontSize: 16,
            },
            body2: {
                fontFamily: '"Short Stack", "Caveat", cursive',
                fontSize: 14,
            },
            caption: {
                fontFamily: '"Short Stack", "Caveat", cursive',
                fontSize: 13,
            },
            button: {
                fontFamily: '"Short Stack", "Caveat", cursive',
                fontWeight: 600,
                textTransform: "none" as const,
                fontSize: 15,
            },
            subtitle1: {
                fontFamily: '"Short Stack", "Caveat", cursive',
            },
            subtitle2: {
                fontFamily: '"Short Stack", "Caveat", cursive',
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 15,
                        boxShadow: "none",
                        "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    root: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 15,
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 15,
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 15,
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 15,
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 14,
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        border: "var(--fq-sketch-border)",
                        borderRadius: "15px 225px 15px 255px / 255px 15px 225px 15px",
                        boxShadow: "4px 4px 0px var(--fq-primary-text-color)",
                        marginTop: 6,
                    },
                    listbox: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 14,
                        "& .MuiAutocomplete-option": {
                            fontFamily: '"Short Stack", "Caveat", cursive',
                            fontSize: 14,
                        },
                    },
                    noOptions: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 14,
                    },
                    tag: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        borderRadius: 8,
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        fontSize: 14,
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        border: "var(--fq-sketch-border)",
                        borderRadius: "15px 225px 15px 255px / 255px 15px 225px 15px",
                        boxShadow: "4px 4px 0px var(--fq-primary-text-color)",
                    },
                    list: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        fontFamily: '"Short Stack", "Caveat", cursive',
                        "& fieldset": {
                            borderRadius: "255px 15px 225px 15px / 15px 225px 15px 255px",
                            border: "var(--fq-sketch-border)",
                        },
                        "&:hover fieldset": {
                            borderColor: "#845ef7",
                        },
                        "&.Mui-focused fieldset": {
                            border: "2px solid #845ef7",
                        },
                    },
                },
            },
        },
    });

    useEffect(() => {
        if (mode === "dark") {
            darkMode();
        } else {
            lightMode();
        }
    }, [mode]);

    return (
        <ThemeContext.Provider value={{ mode, setMode }}>
            <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
        </ThemeContext.Provider>
    );
}

function darkMode() {
    document.documentElement.style.setProperty("--fq-page-bg", "#1e1e2e");
    document.documentElement.style.setProperty("--fq-header-color", "rgba(30, 30, 46, 0.9)");
    document.documentElement.style.setProperty("--fq-border-color-1", "rgba(255, 255, 255, 0.12)");
    document.documentElement.style.setProperty("--fq-warning-red", "rgb(189, 32, 32)");
    document.documentElement.style.setProperty("--fq-warning-orange", "#b87616");
    document.documentElement.style.setProperty("--fq-text-placeholder-color", "rgba(255,255,255,0.6)");
    document.documentElement.style.setProperty("--fq-fetch-note-input-background", "#2a2a3e");
    document.documentElement.style.setProperty("--fq-primary-text-color", "#f0f0f0");
    document.documentElement.style.setProperty("--fq-secondary-text-color", "rgba(240, 240, 240, 0.75)");
    document.documentElement.style.setProperty(
        "--fq-color-text-background",
        "linear-gradient(135deg, #ffb347, #f0a200)",
    );
    document.documentElement.style.setProperty("--fq-disabled-color-button", "#888");
    document.documentElement.style.setProperty("--fq-disabled-background-button", "#2a2a3e");
    document.documentElement.style.setProperty("--fq-disabled-border-button", "2px solid #3a3a4e");
    document.documentElement.style.setProperty("--fq-card-bg", "#2a2a3e");
    document.documentElement.style.setProperty("--fq-card-border", "2px solid rgba(255,255,255,0.15)");
    document.documentElement.style.setProperty("--fq-card-shadow", "4px 4px 0px rgba(255,255,255,0.1)");
    document.documentElement.style.setProperty("--fq-sketch-border", "2px solid rgba(255,255,255,0.35)");
    document.documentElement.style.setProperty("--fq-editor-bg", "#25211a");
    document.documentElement.style.setProperty(
        "--fq-editor-bg-gradient",
        "linear-gradient(160deg, #25211a 0%, #1a1712 100%)",
    );
    document.documentElement.style.setProperty("--fq-surface-elevated", "#2a2a3e");
    document.documentElement.style.setProperty("--fq-background-color-1", "#1e1e2e");
    document.documentElement.style.setProperty("--fq-divider-color", "rgba(255, 255, 255, 0.15)");
}

function lightMode() {
    document.documentElement.style.setProperty("--fq-page-bg", "#fef9ef");
    document.documentElement.style.setProperty("--fq-header-color", "rgba(254, 249, 239, 0.85)");
    document.documentElement.style.setProperty("--fq-border-color-1", "rgba(0, 0, 0, 0.08)");
    document.documentElement.style.setProperty("--fq-warning-red", "rgb(220, 60, 60)");
    document.documentElement.style.setProperty("--fq-warning-orange", "#e8943a");
    document.documentElement.style.setProperty("--fq-text-placeholder-color", "rgba(31, 31, 31, 0.5)");
    document.documentElement.style.setProperty("--fq-fetch-note-input-background", "#ffffff");
    document.documentElement.style.setProperty("--fq-primary-text-color", "#2d2d2d");
    document.documentElement.style.setProperty("--fq-secondary-text-color", "rgba(45, 45, 45, 0.55)");
    document.documentElement.style.setProperty(
        "--fq-color-text-background",
        "linear-gradient(135deg, #845ef7, #e64980)",
    );
    document.documentElement.style.setProperty("--fq-disabled-color-button", "#999");
    document.documentElement.style.setProperty("--fq-disabled-background-button", "#e8e8e8");
    document.documentElement.style.setProperty("--fq-disabled-border-button", "2px solid #ddd");
    document.documentElement.style.setProperty("--fq-card-bg", "#ffffff");
    document.documentElement.style.setProperty("--fq-card-border", "2px solid #2d2d2d");
    document.documentElement.style.setProperty("--fq-card-shadow", "4px 4px 0px #2d2d2d");
    document.documentElement.style.setProperty("--fq-sketch-border", "2px solid #2d2d2d");
    document.documentElement.style.setProperty("--fq-editor-bg", "#fce4e4");
    document.documentElement.style.setProperty(
        "--fq-editor-bg-gradient",
        "linear-gradient(160deg, #fce4e4 0%, #f5d0d0 100%)",
    );
    document.documentElement.style.setProperty("--fq-surface-elevated", "#ffffff");
    document.documentElement.style.setProperty("--fq-background-color-1", "#ffffff");
    document.documentElement.style.setProperty("--fq-divider-color", "rgba(0, 0, 0, 0.12)");
}

interface IGlobalProvider {
    children: React.ReactElement;
}
